import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { RightPanelContent, RightPanelMenuOption } from '../components/right-panel/right-panel.component';

@Injectable({
    providedIn: 'root'
})
export class RightPanelService {
    private readonly router = inject(Router);
    private readonly _isVisible$ = new BehaviorSubject(false);
    private readonly _isExpanded$ = new BehaviorSubject(false);
    private readonly _isCollapsed$ = new BehaviorSubject(false);
    private readonly _content$ = new BehaviorSubject<RightPanelContent | null>(null);
    private readonly _showCloseButton$ = new BehaviorSubject(true);
    private readonly _showExpandableButton$ = new BehaviorSubject(true);

    // Remember the state before hiding to restore it when showing
    private savedExpandedState: boolean = false;
    private savedCollapsedState: boolean = false;
    private isHiding: boolean = false; // Flag to prevent saving state during hide process
    private isProgrammaticChange: boolean = false; // Flag to prevent saving state during programmatic changes

    public constructor() {
        // Track state changes in real-time to always have the latest state saved
        this._isExpanded$.subscribe((expanded) => {
            // Only save when panel is visible and NOT in the process of hiding or programmatic change
            if (this._isVisible$.value && !this.isHiding && !this.isProgrammaticChange) {
                this.savedExpandedState = expanded;
            }
        });

        this._isCollapsed$.subscribe((collapsed) => {
            // Only save when panel is visible and NOT in the process of hiding or programmatic change
            if (this._isVisible$.value && !this.isHiding && !this.isProgrammaticChange) {
                this.savedCollapsedState = collapsed;
            }
        });

        // Reset panel state on navigation
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            // Hide the panel
            if (this._isVisible$.value) {
                this.hide();
            }
            // Reset saved state after navigation
            this.savedExpandedState = false;
            this.savedCollapsedState = false;
        });
    }

    public readonly isVisible$ = this._isVisible$.asObservable();
    public readonly isExpanded$ = this._isExpanded$.asObservable();
    public readonly isCollapsed$ = this._isCollapsed$.asObservable();
    public readonly content$ = this._content$.asObservable();
    public readonly showCloseButton$ = this._showCloseButton$.asObservable();
    public readonly showExpandableButton$ = this._showExpandableButton$.asObservable();

    public show(content: RightPanelContent) {
        this.isHiding = false; // Ensure flag is clear
        this.isProgrammaticChange = false; // Ensure flag is clear
        this._content$.next(content);
        this._isVisible$.next(true);
        // Update showCloseButton if specified in content
        if (content.showCloseButton !== undefined) {
            this._showCloseButton$.next(content.showCloseButton);
        }
        // Update showExpandableButton if specified in content
        if (content.showExpandableButton !== undefined) {
            this._showExpandableButton$.next(content.showExpandableButton);
        }

        // Restore saved state from before hiding
        this._isExpanded$.next(this.savedExpandedState);
        this._isCollapsed$.next(this.savedCollapsedState);
    }

    public hide() {
        // Set flag to prevent subscriptions from saving state during close
        this.isHiding = true;
        this.isProgrammaticChange = false; // Ensure flag is clear

        // Save current state BEFORE any changes
        this.savedExpandedState = this._isExpanded$.value;
        this.savedCollapsedState = this._isCollapsed$.value;

        // Now hide and reset
        this._isVisible$.next(false);
        this._isExpanded$.next(false);
        this._isCollapsed$.next(false);

        // Clear content after animation
        setTimeout(() => {
            this._content$.next(null);
            this.isHiding = false; // Reset flag after hiding is complete
        }, 300);
    }

    public toggle(content?: RightPanelContent) {
        if (this._isVisible$.value) {
            this.hide();
        } else if (content) {
            this.show(content);
        }
    }

    public setExpanded(expanded: boolean) {
        this._isExpanded$.next(expanded);
        // If expanding, make sure we're not collapsed
        if (expanded) {
            this._isCollapsed$.next(false);
        }
    }

    public toggleExpanded() {
        this._isExpanded$.next(!this._isExpanded$.value);
        // If expanding, make sure we're not collapsed
        if (this._isExpanded$.value) {
            this._isCollapsed$.next(false);
        }
    }

    public setCollapsed(collapsed: boolean) {
        this._isCollapsed$.next(collapsed);
        // If collapsing, make sure we're not expanded
        if (collapsed) {
            this._isExpanded$.next(false);
        }
    }

    public toggleCollapsed() {
        this._isCollapsed$.next(!this._isCollapsed$.value);
        // If collapsing, make sure we're not expanded
        if (this._isCollapsed$.value) {
            this._isExpanded$.next(false);
        }
    }

    public setShowCloseButton(show: boolean) {
        this._showCloseButton$.next(show);
    }

    public setShowExpandableButton(show: boolean) {
        this._showExpandableButton$.next(show);
    }

    // Utility methods for easier component usage
    public showComponent<T>(
        title: string,
        componentType: new (...args: unknown[]) => T,
        data?: Record<string, unknown>,
        showCloseButton?: boolean,
        showExpandableButton?: boolean,
        showCollapseToggle?: boolean,
        icon?: string
    ) {
        this.show({
            title,
            icon,
            component: componentType as RightPanelContent['component'],
            data,
            showCloseButton,
            showExpandableButton,
            showCollapseToggle
        });
    }

    // Show component with menu options
    public showComponentWithMenu<T>(
        title: string,
        componentType: new (...args: unknown[]) => T,
        data?: Record<string, unknown>,
        menuOptions?: RightPanelMenuOption[],
        showCloseButton?: boolean,
        showExpandableButton?: boolean,
        showCollapseToggle?: boolean,
        icon?: string,
        startCollapsed: boolean = false
    ) {
        // Check if panel is already visible
        const wasVisible = this._isVisible$.value;

        this.show({
            title,
            icon,
            component: componentType as RightPanelContent['component'],
            data,
            menuOptions,
            showCloseButton,
            showExpandableButton,
            showCollapseToggle
        });

        // Apply startCollapsed override in specific cases:
        // 1. Panel wasn't visible before (first open) - respect startCollapsed setting
        // 2. Panel was visible and startCollapsed is false - ensure it's not collapsed
        if (!wasVisible) {
            // First open - apply startCollapsed setting without saving it
            if (startCollapsed) {
                this.isProgrammaticChange = true;
                this.setCollapsed(true);
                this.isProgrammaticChange = false;
            }
        } else if (!startCollapsed && this._isCollapsed$.value) {
            // Panel is visible and collapsed, but startCollapsed is false - uncollapse it
            this.isProgrammaticChange = true;
            this.setCollapsed(false);
            this.isProgrammaticChange = false;
        }
    }

    // Quick method to show simple content
    public showContent(title: string, message: string) {
        this.show({
            title,
            data: { message }
        });
    }
}
